import { forwardRef, useEffect, useRef, useState, type ReactNode } from "react";
import { fetchMedicalEvidence, UnauthorizedError } from "@/lib/clinician-api";
import { missingBasis } from "@/lib/missing-basis";

// TES-1019 (Emelie, point 2) — the first of the note's three levels:
// prioritised risk flags and missing basis, visible above the journal boxes
// instead of in the side sheet. Collapsible, open by default. The flags are
// the same list and order as everywhere else (RiskFlagsPanel, passed in by
// the caller); the missing basis is the open completion checklist's rows
// that are not done (lib/missing-basis). The second level is the boxes, the
// third each box's source.

export const FlagsAndMissingBasis = forwardRef<
  HTMLDetailsElement,
  {
    patientId: string;
    /** Bumped after a publish, so the checklist is read again. */
    reloadKey: number;
    flags: ReactNode;
    open: boolean;
    onToggle: (open: boolean) => void;
    onUnauthorized: () => void;
  }
>(function FlagsAndMissingBasis(
  { patientId, reloadKey, flags, open, onToggle, onUnauthorized },
  ref,
) {
  const [missing, setMissing] = useState<string[] | null>(null);
  const [error, setError] = useState(false);
  const onUnauthorizedRef = useRef(onUnauthorized);
  onUnauthorizedRef.current = onUnauthorized;

  useEffect(() => {
    let live = true;
    setError(false);
    fetchMedicalEvidence(patientId)
      .then((d) => {
        if (live) setMissing(missingBasis(d));
      })
      .catch((e) => {
        if (!live) return;
        if (e instanceof UnauthorizedError) onUnauthorizedRef.current();
        else setError(true);
      });
    return () => {
      live = false;
    };
  }, [patientId, reloadKey]);

  return (
    <details
      ref={ref}
      open={open}
      onToggle={(e) => onToggle(e.currentTarget.open)}
      className="rounded-lg bg-surface p-md shadow-[0_1px_2px_rgba(42,26,40,0.06),0_8px_24px_-16px_rgba(97,32,95,0.25)]"
      data-testid="flags-and-missing-basis"
    >
      <summary className="cursor-pointer font-display text-[18px] leading-7 text-textPrimary">
        Riskflaggor och saknat underlag
      </summary>
      <div className="mt-sm flex flex-col gap-md">
        {flags}
        <section aria-labelledby="missing-basis-heading">
          <h3
            id="missing-basis-heading"
            className="text-sm font-semibold text-textPrimary"
          >
            Saknat underlag
          </h3>
          {error ? (
            <p className="mt-xs text-sm text-danger">
              Kunde inte hämta kompletteringarna.
            </p>
          ) : missing === null ? (
            <p className="mt-xs text-sm text-textSecondary">Hämtar…</p>
          ) : missing.length === 0 ? (
            <p className="mt-xs text-sm text-textSecondary">
              Inga öppna kompletteringar.
            </p>
          ) : (
            <ul className="mt-xs list-disc pl-lg text-sm text-textPrimary">
              {missing.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </details>
  );
});
